import React, { createContext, useState, useCallback, ReactNode } from 'react';
import { Snackbar } from '../../navigation/snackbar/Snackbar';
import { SnackbarMessage, SnackbarSeverity, SnackbarAction, SnackbarContextValue } from '../../navigation/snackbar/types';

export const SnackbarContext = createContext<SnackbarContextValue | undefined>(undefined);

interface SnackbarProviderProps {
  children: ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [queue, setQueue] = useState<SnackbarMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState<SnackbarMessage | null>(null);

  // Process queue when current message is cleared
  React.useEffect(() => {
    if (!currentMessage && queue.length > 0) {
      const [nextMessage, ...remainingQueue] = queue;
      setCurrentMessage(nextMessage);
      setQueue(remainingQueue);
    }
  }, [currentMessage, queue]);

  const showSnackbar = useCallback(
    (
      message: string,
      severity: SnackbarSeverity,
      options?: {
        action?: SnackbarAction;
        hideDelay?: number;
      }
    ) => {
      const newMessage: SnackbarMessage = {
        id: `snackbar-${Date.now()}-${Math.random()}`,
        message,
        severity,
        action: options?.action,
        hideDelay: options?.hideDelay || 3000,
      };
      setQueue(prev => [...prev, newMessage]);
    },
    []
  );

  const hideSnackbar = useCallback(() => {
    setCurrentMessage(null);
  }, []);

  const contextValue: SnackbarContextValue = {
    showSnackbar,
    hideSnackbar,
  };

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar message={currentMessage} onClose={hideSnackbar} />
    </SnackbarContext.Provider>
  );
};
